import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import DemoModal from './DemoModal';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'Expertise', href: '#problem' },
    { name: 'Advies', href: '#selector' },
    { name: 'Services', href: '#services' },
    { name: 'Portfolio', href: '#proof' },
  ];

  const menuVariants = {
    closed: { opacity: 0, y: "-100%" },
    open: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
  };

  const linkVariants = {
    closed: { opacity: 0, y: 20 },
    open: (i: number) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { delay: 0.1 + i * 0.1, duration: 0.5, ease: "easeOut" } 
    })
  };

  const handleBookingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setDemoModalOpen(true);
  };

  return (
    <>
      <DemoModal 
        isOpen={demoModalOpen} 
        onClose={() => setDemoModalOpen(false)} 
        type="booking"
      />

      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${scrolled ? 'bg-black/90 backdrop-blur-md py-4 border-white/10' : 'bg-transparent py-6 border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center relative z-50">
          {/* Logo */}
          <div className="text-2xl font-display font-bold tracking-tighter text-white select-none cursor-pointer relative z-50" onClick={() => { setMobileMenuOpen(false); window.scrollTo(0,0); }}>
            OBSIDIAN<span className="text-neutral-500">.</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white transition-colors"
              >
                {link.name}
              </a>
            ))}
            <button 
              onClick={() => setDemoModalOpen(true)}
              className="text-xs uppercase tracking-widest bg-white text-black px-6 py-3 hover:bg-neutral-200 transition-colors font-medium"
            >
              Plan Intake
            </button>
          </div>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden text-white relative z-50 p-2 -mr-2 focus:outline-none" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <AnimatePresence mode="wait">
              {mobileMenuOpen ? (
                <motion.div key="close" initial={{ opacity: 0, rotate: -90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: 90 }}>
                  <X size={24} />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ opacity: 0, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: -90 }}>
                  <Menu size={24} />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      {/* Mobile Full Screen Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="fixed inset-0 z-40 bg-neutral-950/98 backdrop-blur-xl md:hidden flex flex-col justify-center px-6"
          >
            <div className="flex flex-col gap-8 max-w-sm mx-auto w-full">
              {navLinks.map((link, i) => (
                <motion.a 
                  key={link.name} 
                  custom={i}
                  variants={linkVariants}
                  href={link.href} 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-3xl font-display text-white hover:text-neutral-400 transition-colors tracking-wide"
                >
                  {link.name}
                </motion.a>
              ))}
              
              <motion.div 
                custom={navLinks.length}
                variants={linkVariants}
                className="h-px w-full bg-white/10 my-4"
              />

              <motion.div
                custom={navLinks.length + 1}
                variants={linkVariants}
              >
                <Button 
                  fullWidth 
                  onClick={handleBookingClick}
                >
                  Plan Intake <ArrowRight size={16} />
                </Button>
                
                <p className="text-neutral-500 text-xs text-center mt-6 uppercase tracking-widest">
                  Est. 2024 — Amsterdam
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;